import { useProgressStore } from '../../../store/progress';

export type VoiceLine = 'intro' | 'hint' | 'success';

let unlocked = false;
let activeUtterance: SpeechSynthesisUtterance | null = null;

/** Call from the first user gesture (iOS Safari audio unlock). */
export function unlockAudio(): void {
  unlocked = true;
  if (typeof window === 'undefined') return;
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctx) {
      const ctx = new Ctx();
      void ctx.resume();
    }
  } catch {
    // Silently skip if unavailable.
  }
}

function voiceFilePath(levelId: string, line: VoiceLine): string {
  return `/audio/voice/${levelId}-${line}.mp3`;
}

/**
 * Plays nb-NO voice lines. Prefers recorded mp3; falls back to Web Speech API.
 * Respects global mute. Silently skips if speech is unavailable.
 */
export async function playVoice(
  levelId: string,
  line: VoiceLine,
  text: string
): Promise<void> {
  const muted = useProgressStore.getState().muted;
  if (muted || typeof window === 'undefined') return;

  if (!unlocked) unlockAudio();

  stopVoice();

  const path = voiceFilePath(levelId, line);
  try {
    const audio = new Audio(path);
    await new Promise<void>((resolve, reject) => {
      audio.onended = () => resolve();
      audio.onerror = () => reject(new Error('missing audio'));
      void audio.play().catch(reject);
    });
    return;
  } catch {
    // Fall through to Web Speech.
  }

  return speakNow(text);
}

/** Short spoken line with no recorded file (praise, garage). Respects mute. */
export function speak(text: string): Promise<void> {
  const muted = useProgressStore.getState().muted;
  if (muted || typeof window === 'undefined') return Promise.resolve();
  return speakNow(text);
}

function speakNow(text: string): Promise<void> {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return Promise.resolve();

  return new Promise((resolve) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'nb-NO';
    utterance.rate = 0.95;
    utterance.onend = () => {
      if (activeUtterance === utterance) activeUtterance = null;
      resolve();
    };
    utterance.onerror = () => {
      if (activeUtterance === utterance) activeUtterance = null;
      resolve();
    };
    activeUtterance = utterance;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  });
}

export function stopVoice(): void {
  if (typeof window === 'undefined') return;
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
  activeUtterance = null;
}
